import {
  useEffect,
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { Award, Send, Eye, Pencil, FileText, Loader2, Download, Check, X, Plus, Info, ShieldAlert, Trash2, RefreshCw } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useInsuredCOIHistoryPage, useInsuredCOISenders, useInsuredCoiNotes, useUpsertInsuredCoiNotes, useFeatureFlag } from "@/lib/api";
import { formatDate, formatDateInput, parseDateFilter } from "@/lib/formatters";
import { CoiTemplateEditor } from "@/components/CoiTemplateEditor";
import { COI_ELIGIBLE_STATUSES, isCoiSelectablePolicy, type Insured, type Policy, type CoiNote } from "@shared/schema";
import { FLAG_COI_POLICY_PICKER } from "@shared/feature-flags";
import { COI_NO_SELECTABLE_POLICY_REASON, withoutAorPolicies } from "../coi/policySelection";
import { AOR_COI_NO_SENDABLE_POLICY_REASON } from "../policies/aor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SendCOIDialog } from "../SendCOIDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { buildResendConfig } from "@/lib/coiResend";
import type { CoiSendConfig } from "@shared/coi-template";

interface Props {
  insured: Insured;
  policies: Policy[];
  showAor?: boolean;
}

// CSR-2237: matches the COI Requests table in System Settings. A book of
// certificates only grows — this tab used to render every row it had.
const COI_HISTORY_PAGE_SIZE = 25;

// CSR-2287: the shared button style makes a disabled button drop pointer events,
// and a disabled button cannot take focus, so the reason on `title` reaches
// neither hover nor the keyboard. This focusable wrapper carries it instead, and
// is what an enclosing tooltip opens from.
function SendGateReason({
  reason,
  testId,
  children,
  ...rest
}: {
  reason: string | undefined;
  testId: string;
  children: ReactNode;
} & ComponentPropsWithoutRef<"span">) {
  if (!reason) return <>{children}</>;
  const reasonId = `${testId}-reason`;
  return (
    <span
      {...rest}
      tabIndex={0}
      role="group"
      aria-describedby={reasonId}
      className="inline-flex"
      data-testid={testId}
    >
      {children}
      <span id={reasonId} className="sr-only">
        {reason}
      </span>
    </span>
  );
}

export function COIsTab({ insured, policies, showAor = false }: Props) {
  const [sendCOIOpen, setSendCOIOpen] = useState(false);
  const [resendConfig, setResendConfig] = useState<CoiSendConfig | null>(null);
  // CSR-2287: with the picker on, a send needs a term the picker would let the
  // producer put on a certificate — which a future renewal offer is and an
  // expired or non-renewed term is not, whatever COI eligibility says.
  const policyPickerEnabled = useFeatureFlag(FLAG_COI_POLICY_PICKER);
  const hasSendablePolicy = (list: Policy[]) =>
    policyPickerEnabled
      ? list.some((p) => isCoiSelectablePolicy(p))
      : list.some((p) => COI_ELIGIBLE_STATUSES.has((p.status || "").toLowerCase()));
  // CSR-2333: an AOR'd policy never goes on a certificate.
  const certificatePolicies = useMemo(
    () => withoutAorPolicies(policies, { excludeAor: showAor }),
    [policies, showAor],
  );
  const canSendCoi = hasSendablePolicy(certificatePolicies);
  const noSelectablePolicyReason = canSendCoi
    ? undefined
    : hasSendablePolicy(policies)
      ? AOR_COI_NO_SENDABLE_POLICY_REASON
      : policyPickerEnabled
        ? COI_NO_SELECTABLE_POLICY_REASON
        : undefined;
  const [editorPolicyId, setEditorPolicyId] = useState<string | null>(null);
  const { data: coiNotesData } = useInsuredCoiNotes(insured.id);
  const { mutate: upsertCoiNotes } = useUpsertInsuredCoiNotes();
  const [addingNote, setAddingNote] = useState(false);
  const [newNoteType, setNewNoteType] = useState<"info" | "rule">("info");
  const [newNoteContent, setNewNoteContent] = useState("");
  const notes: CoiNote[] = (coiNotesData?.coiNotes as CoiNote[] | null) ?? [];
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);

  const [holderSearch, setHolderSearch] = useState("");
  const [debouncedHolder, setDebouncedHolder] = useState("");
  const [sentFrom, setSentFrom] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [sentBy, setSentBy] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedHolder(holderSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [holderSearch]);

  const [page, setPage] = useState(1);

  const filters = useMemo(() => ({
    holder: debouncedHolder || undefined,
    sentFrom: parseDateFilter(sentFrom) || undefined,
    sentTo: parseDateFilter(sentTo) || undefined,
    sentBy: sentBy || undefined,
  }), [debouncedHolder, sentFrom, sentTo, sentBy]);

  // A narrowed list is shorter, so page 4 of the old one is usually past the
  // end of the new — which would show an empty table for a filter that matches
  // plenty.
  useEffect(() => {
    setPage(1);
  }, [filters]);

  const hasActiveFilters = !!(holderSearch || sentFrom || sentTo || sentBy);

  const clearFilters = () => {
    setHolderSearch("");
    setDebouncedHolder("");
    setSentFrom("");
    setSentTo("");
    setSentBy("");
  };

  const { data: coiHistory, isLoading, isPlaceholderData } = useInsuredCOIHistoryPage(
    insured.id,
    page,
    COI_HISTORY_PAGE_SIZE,
    filters,
  );
  const issuances = coiHistory?.data ?? [];
  const total = coiHistory?.total ?? 0;
  // The page size the SERVER used, not the one we asked for. A COI service
  // deployed before CSR-2237 ignores the page params and answers with the whole
  // history, which arrives as one full page — sized to the rows in it. Reading
  // the constant instead would then draw a pager over an unpaginated table:
  // "1–25 of 129" above 129 rows, and a Next button that re-labels the same
  // rows without changing them.
  const pageSize = coiHistory?.pageSize || COI_HISTORY_PAGE_SIZE;
  // Likewise the page the rows on screen came from, not the one just clicked.
  // `keepPreviousData` holds the old rows through the refetch, so labelling
  // them with the requested page would caption rows 1-25 as "26-50" for as
  // long as the request takes.
  const shownPage = coiHistory?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showFooter = total > pageSize;
  const { data: senders } = useInsuredCOISenders(insured.id);

  const eligiblePolicy = useMemo(
    () =>
      certificatePolicies.find(
        (p) => COI_ELIGIBLE_STATUSES.has(p.status?.toLowerCase() ?? "") && !p.deletedAt,
      ) ?? null,
    [certificatePolicies],
  );

  return (
    <div className="space-y-6" data-testid="cois-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Award className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Certificates of Insurance</h3>
            <p className="text-xs text-muted-foreground" data-testid="cois-count">
              {total} certificate{total !== 1 ? "s" : ""} sent
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="rounded-xl gap-2"
            disabled={!eligiblePolicy}
            onClick={() => eligiblePolicy && setEditorPolicyId(eligiblePolicy.id)}
            data-testid="cois-view-template"
          >
            <Eye className="w-4 h-4" /> View / Edit Template
          </Button>
          <SendGateReason
            reason={noSelectablePolicyReason}
            testId="cois-send-new-gate"
          >
            <Button
              className="rounded-xl gap-2 shadow-none"
              disabled={!canSendCoi}
              title={noSelectablePolicyReason}
              onClick={() => { setResendConfig(null); setSendCOIOpen(true); }}
              data-testid="cois-send-new"
            >
              <Send className="w-4 h-4" /> Send New COI
            </Button>
          </SendGateReason>
        </div>
      </div>

      {/* COI Rules & Notes */}
      <Card className="p-5 border-border shadow-none" data-testid="cois-rules-notes">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-600" />
            <h4 className="font-bold text-sm">COI Rules & Notes</h4>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">
              AI + Human
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-lg text-xs gap-1 h-7"
            onClick={() => { setAddingNote(true); setNewNoteType("info"); setNewNoteContent(""); }}
          >
            <Plus className="w-3 h-3" /> Add Note
          </Button>
        </div>

        {addingNote && (
          <div className="mb-3 rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2">
            <div className="flex gap-2">
              {(["info", "rule"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setNewNoteType(t)}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-md border text-xs font-medium transition-colors cursor-pointer ${
                    newNoteType === t
                      ? t === "rule"
                        ? "bg-red-100 text-red-700 border-red-300"
                        : "bg-blue-100 text-blue-700 border-blue-300"
                      : "bg-background border-input hover:bg-muted"
                  }`}
                >
                  {t === "info" ? <Info className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                  {t === "info" ? "Information" : "Rule"}
                </button>
              ))}
            </div>
            <textarea
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              rows={2}
              placeholder={newNoteType === "rule" ? "e.g. Do NOT send COIs to FedEx — customer has separate arrangement." : "e.g. Customer prefers email confirmation after every COI is sent."}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              autoFocus
            />
            <div className="flex justify-end gap-1.5">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAddingNote(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs gap-1"
                disabled={!newNoteContent.trim()}
                onClick={() => {
                  const updated = [...notes, { id: crypto.randomUUID().slice(0, 8), type: newNoteType, content: newNoteContent.trim() }];
                  upsertCoiNotes({ insuredId: insured.id, coiNotes: updated }, { onSuccess: () => setAddingNote(false) });
                }}
              >
                <Check className="w-3 h-3" /> Add
              </Button>
            </div>
          </div>
        )}

        {notes.length > 0 ? (
          <div className="space-y-2">
            {[...notes].sort((a, b) => (a.type === b.type ? 0 : a.type === "rule" ? -1 : 1)).map((note) => (
              <div
                key={note.id}
                className={`flex items-start gap-3 rounded-xl p-3 text-sm ${
                  note.type === "rule"
                    ? "bg-red-50 border border-red-200/50"
                    : "bg-amber-500/5 border border-amber-200/30"
                }`}
              >
                {note.type === "rule" ? (
                  <ShieldAlert className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                ) : (
                  <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                )}
                <span className="flex-1 leading-relaxed">{note.content}</span>
                {note.source === "nowcerts_import" && (
                  <span
                    data-testid={`coi-note-imported-badge-${note.id}`}
                    className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-500 shrink-0"
                  >
                    Imported
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-muted-foreground hover:text-red-500"
                  onClick={() => setDeleteNoteId(note.id)}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        ) : !addingNote ? (
          <div className="bg-muted/30 border border-border/30 rounded-xl p-4 text-sm text-muted-foreground italic text-center">
            No COI rules set. Click "Add Note" to add rules and notes for this insured's certificate handling.
          </div>
        ) : null}
      </Card>

      <AlertDialog open={!!deleteNoteId} onOpenChange={(open) => !open && setDeleteNoteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (!deleteNoteId) return;
                const updated = notes.filter((n) => n.id !== deleteNoteId);
                upsertCoiNotes({ insuredId: insured.id, coiNotes: updated });
                setDeleteNoteId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Filters */}
      <Card className="p-3 border-border shadow-none" data-testid="cois-filters">
        <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_1fr] gap-2 items-end">
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              Certificate Holder
            </label>
            <Input className="mt-1 h-9" placeholder="Search holder..." value={holderSearch} onChange={(e) => setHolderSearch(e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              Sent From
            </label>
            <Input className="mt-1 h-9" placeholder="MM/DD/YYYY" value={sentFrom} onChange={(e) => setSentFrom(formatDateInput(e.target.value))} maxLength={10} />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              Sent To
            </label>
            <Input className="mt-1 h-9" placeholder="MM/DD/YYYY" value={sentTo} onChange={(e) => setSentTo(formatDateInput(e.target.value))} maxLength={10} />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              Sent By
            </label>
            <select
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              value={sentBy}
              onChange={(e) => setSentBy(e.target.value)}
            >
              <option value="">All</option>
              {(senders ?? []).map((sender) => (
                <option key={sender.id} value={sender.id}>{sender.name}</option>
              ))}
            </select>
          </div>
        </div>
        {hasActiveFilters && (
          <div className="flex justify-end mt-2">
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        )}
      </Card>

      {/* History Table */}
      <Card className="border-border shadow-none overflow-hidden" data-testid="cois-table">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b border-border">
            <tr>
              <th className="px-5 py-3 font-semibold">Certificate Holder</th>
              <th className="px-5 py-3 font-semibold">To Email</th>
              <th className="px-5 py-3 font-semibold">Additional Information</th>
              <th className="px-5 py-3 font-semibold">Sent Date</th>
              <th className="px-5 py-3 font-semibold">Sent By</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold text-right">PDF</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-5 py-12 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mx-auto" />
                </td>
              </tr>
            ) : issuances.length === 0 && hasActiveFilters ? (
              <tr>
                <td colSpan={7} className="px-5 py-12 text-center text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <Award className="w-10 h-10 text-muted-foreground/40" />
                    <p>No certificates match the current filters</p>
                  </div>
                </td>
              </tr>
            ) : issuances.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-12 text-center text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <Award className="w-10 h-10 text-muted-foreground/40" />
                    <p>No certificates sent yet</p>
                    <SendGateReason
                      reason={noSelectablePolicyReason}
                      testId="cois-send-first-gate"
                    >
                      <Button
                        size="sm"
                        className="mt-2 gap-1.5"
                        disabled={!canSendCoi}
                        title={noSelectablePolicyReason}
                        onClick={() => setSendCOIOpen(true)}
                      >
                        <Send className="w-3.5 h-3.5" /> Send First COI
                      </Button>
                    </SendGateReason>
                  </div>
                </td>
              </tr>
            ) : (
              issuances.map((issuance) => {
                const sentDate = new Date(issuance.issuedAt as unknown as string);
                // CSR-2287: hoisted because `TooltipTrigger asChild` needs ONE
                // child element — the gate wrapper when there is a reason, this
                // button itself when there is not.
                const resendButton = (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 cursor-pointer"
                    disabled={!!noSelectablePolicyReason}
                    title={noSelectablePolicyReason}
                    data-testid={`cois-resend-${issuance.id}`}
                    onClick={() => {
                      setResendConfig(buildResendConfig(issuance, insured));
                      setSendCOIOpen(true);
                    }}
                  >
                    <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  </Button>
                );
                return (
                <tr key={issuance.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center text-primary text-[10px] font-bold shrink-0">
                        {(issuance.certificateHolderName || "?").charAt(0)}
                      </div>
                      <span className="font-medium text-sm">{issuance.certificateHolderName}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    {(issuance.redirectedRecipientEmail ?? issuance.recipientEmail) ? (
                      <span className="text-primary text-xs">
                        {issuance.redirectedRecipientEmail ?? issuance.recipientEmail}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1 flex-wrap">
                      {(() => {
                        // CSR-751: prefer the structured selections the user
                        // picked in the send-COI modal. Falls back to the
                        // legacy boolean fields for issuances created before
                        // CSR-751 (or by external paths that don't set the
                        // new column, e.g. the intercom worker).
                        const selections = Array.isArray(issuance.additionalInfo)
                          ? issuance.additionalInfo
                          : null;
                        const tagClass = "px-2 py-0.5 rounded text-[10px] font-semibold";
                        const tagStyles: Record<string, string> = {
                          "Vehicles": "bg-sky-100 text-sky-700",
                          "Trailers": "bg-indigo-100 text-indigo-700",
                          "Drivers": "bg-emerald-100 text-emerald-700",
                          "Additional Insured": "bg-amber-100 text-amber-700",
                          "Loss Payee": "bg-rose-100 text-rose-700",
                          "Special Verbiage": "bg-blue-100 text-blue-700",
                        };
                        if (selections && selections.length > 0) {
                          return selections.map((label) => (
                            <span
                              key={label}
                              className={`${tagClass} ${tagStyles[label] ?? "bg-slate-100 text-slate-700"}`}
                            >
                              {label}
                            </span>
                          ));
                        }
                        // Legacy fallback for older rows without additionalInfo.
                        const legacy: { key: string; label: string; cls: string }[] = [];
                        if (issuance.additionalInsured) {
                          legacy.push({ key: "ai", label: "Additional Insured", cls: tagStyles["Additional Insured"] });
                        }
                        if (issuance.waiverOfSubrogation) {
                          legacy.push({ key: "wos", label: "WoS", cls: "bg-violet-100 text-violet-700" });
                        }
                        if (issuance.description) {
                          legacy.push({ key: "desc", label: "Description", cls: "bg-slate-100 text-slate-700" });
                        }
                        if (legacy.length === 0) return <>—</>;
                        return legacy.map((t) => (
                          <span key={t.key} className={`${tagClass} ${t.cls}`}>
                            {t.label}
                          </span>
                        ));
                      })()}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-muted-foreground text-xs">
                    {sentDate.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}
                    {" "}
                    {sentDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-5 py-3.5 text-sm">{issuance.issuedByName || "System"}</td>
                  <td className="px-5 py-3.5">
                    {(() => {
                      // CSR-758: the issuance row carries `deliveryStatus`
                      // ('processing' | 'sent' | 'failed'). Pre-CSR-758
                      // historical rows backfilled to 'sent' via DB default.
                      const status = issuance.deliveryStatus || "sent";
                      if (status === "processing") {
                        return (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            PROCESSING
                          </span>
                        );
                      }
                      if (status === "failed") {
                        return (
                          <span
                            className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700"
                            title={issuance.errorMessage ?? undefined}
                          >
                            FAILED
                          </span>
                        );
                      }
                      return (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">
                          SENT
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {noSelectablePolicyReason ? (
                              <SendGateReason
                                reason={noSelectablePolicyReason}
                                testId={`cois-resend-gate-${issuance.id}`}
                              >
                                {resendButton}
                              </SendGateReason>
                            ) : (
                              resendButton
                            )}
                          </TooltipTrigger>
                          <TooltipContent>Resend COI</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={!issuance.documentId}
                        onClick={() => issuance.documentId && setPreviewDocId(issuance.documentId)}
                      >
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={!issuance.documentId}
                        onClick={() => issuance.documentId && window.open(`/api/documents/${issuance.documentId}/download`, "_blank")}
                      >
                        <Download className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
        {showFooter && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border" data-testid="cois-pagination">
            <p className="text-sm text-muted-foreground">
              {((shownPage - 1) * pageSize + 1).toLocaleString()}&ndash;
              {Math.min(shownPage * pageSize, total).toLocaleString()} of{" "}
              {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                onClick={() => setPage(1)}
                disabled={page === 1 || isPlaceholderData}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isPlaceholderData}
              >
                Previous
              </Button>
              <span className="text-sm px-3">
                Page {shownPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isPlaceholderData}
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages || isPlaceholderData}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </Card>

      {previewDocId && (
        <Dialog open onOpenChange={(open) => { if (!open) setPreviewDocId(null); }}>
          <DialogContent className="max-w-4xl w-[90vw] h-[85vh] flex flex-col p-0 overflow-hidden" aria-describedby={undefined}>
            <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
              <DialogTitle className="flex items-center gap-2 text-base">
                <FileText className="w-4 h-4" />
                Certificate of Insurance
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 px-6 pb-6">
              <iframe
                src={`/api/documents/${previewDocId}/view`}
                className="w-full h-full rounded-lg border"
                title="COI Preview"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {editorPolicyId && (
        <CoiTemplateEditor
          open
          onClose={() => setEditorPolicyId(null)}
          policyId={editorPolicyId}
        />
      )}

      <SendCOIDialog
        open={sendCOIOpen}
        onOpenChange={(o) => { setSendCOIOpen(o); if (!o) setResendConfig(null); }}
        insured={insured}
        policies={policies}
        showAor={showAor}
        resendConfig={resendConfig}
      />
    </div>
  );
}
